/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useSWRInfinite from 'swr/infinite';
import { Avatar } from '@components/avatar';
import { AlertDialog, Text, Box, Step, Button } from '@components/design-system';
import { FollowUserButton } from '@components/follow-user-button';
import { IUser } from '@interface/token';
import { getURLFromIPFSorHTTP } from '@utils/ipfs';
import { getScreenName } from '@utils/user';
import * as Styles from './styles';

export interface IUserFollowing {
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  userid?: string;
  viewer?: string;
}

const fetcher = (url: string) =>
  fetch(url)
    .then((res) => res.json())
    .then((e) => {
      return e.users;
    });

const LIMIT = 30;

export const UserFollowing: FC<IUserFollowing> = ({ userid, viewer, open, onOpenChange }) => {
  const router = useRouter();
  const { data, error, size, setSize } = useSWRInfinite(
    (i: number, previousPageData: any) => {
      if (previousPageData && !previousPageData.length) return null;
      return `/api/public/user/${userid}/following?viewer=${viewer}&limit=${LIMIT}&offset=${(i + 0) * LIMIT}`;
    },
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
    }
  );

  const items = data ? [].concat(...data) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data && data.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.length < LIMIT);

  return (
    <AlertDialog title="Following" open={open} onOpenChange={onOpenChange}>
      <Styles.Content>
        {!isLoadingInitialData && items.length === 0 && (
          <Text unset css={{ color: '$secondaryFill' }}>
            No data to display
          </Text>
        )}

        {items.map((u: IUser) => {
          const versumScreenName = getScreenName(u.verified_on_chain, u.screen_name, u.id, u.name);

          return (
            <Styles.User key={u.id}>
              <Link key={u.id} href={`/user/${u.id}/created`} locale={router.locale} prefetch={false} passHref>
                <a>
                  <Styles.Profile>
                    <Avatar username={u.id} path={getURLFromIPFSorHTTP(u.profile_image_uri)} size={32} />
                    <Styles.Info>
                      <Text>
                        <strong>{versumScreenName}</strong>
                      </Text>
                      <Text unset css={{ color: '$secondaryFill' }}>
                        {u.name}
                      </Text>
                    </Styles.Info>
                  </Styles.Profile>
                </a>
              </Link>

              {viewer !== u.name && (
                <FollowUserButton viewer={viewer} userId={u.id} isFollowing={u.followedByMe.length > 0} />
              )}
            </Styles.User>
          );
        })}

        {!isReachingEnd && (
          <Box css={{ padding: '$1 0' }} horizontal>
            {isLoadingMore ? (
              <Step progress />
            ) : (
              <Button onClick={() => setSize(size + 1)} variant="secondary">
                Load more
              </Button>
            )}
          </Box>
        )}
      </Styles.Content>
    </AlertDialog>
  );
};
